import type { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

/**
 * State interface for the PT Activation Store
 *
 * Manages the state of the PT activation and deactivation warning dialogs, including:
 * - Whether the dialogs are currently open
 * - The PT node and name to display (for activation)
 * - The content item ID that triggered the warning
 * - Actions to open/close the dialogs
 */
interface PTActivationState {
    /** Whether the PT activation warning dialog is currently displayed */
    isDialogOpen: boolean

    /** The inactive PT element node */
    ptElementNode: ProjectElementNode | null

    /** Display names of the PT(s) as an array */
    ptName: string[]

    /** Content item ID that triggered the warning */
    contentItemId: string | null

    /** Callback to execute when user proceeds with activation */
    onProceedCallback: (() => Promise<void>) | null

    /** Whether the PT deactivation warning dialog is currently displayed */
    isDeactivationDialogOpen: boolean

    /** Callback to execute when user proceeds with deactivation */
    deactivationOnProceedCallback: (() => Promise<void>) | null

    /** Whether the contributor restriction dialog is currently displayed */
    isContributorRestrictionDialogOpen: boolean

    /** Updated element nodes from PT activation mutation - to trigger UI update */
    updatedElementNodes: ProjectElementNode[] | null

    /** Whether the PT activation progress dialog is displayed */
    isProgressDialogOpen: boolean

    /** PT name being activated (for progress display) */
    activatingPTName: string

    /** Sets the updated element nodes after PT activation */
    setUpdatedElementNodes: (nodes: ProjectElementNode[] | null) => void

    /** Opens the PT activation progress dialog */
    openProgressDialog: (ptName: string) => void

    /** Closes the PT activation progress dialog */
    closeProgressDialog: () => void

    /**
     * Opens the PT activation warning dialog
     * @param ptElementNode - The inactive PT element node
     * @param ptName - Display names of the PT(s) as an array
     * @param contentItemId - Content item ID that triggered the warning
     * @param onProceed - Callback when user clicks Proceed
     */
    openDialog: (
        ptElementNode: ProjectElementNode,
        ptName: string[],
        contentItemId: string,
        onProceed: () => Promise<void>
    ) => void

    /**
     * Closes the PT activation warning dialog and resets state
     */
    closeDialog: () => void

    /**
     * Opens the PT deactivation warning dialog
     * @param onProceed - Callback when user clicks Yes
     */
    openDeactivationDialog: (onProceed: () => Promise<void>) => void

    /**
     * Closes the PT deactivation warning dialog and resets state
     */
    closeDeactivationDialog: () => void

    /**
     * Opens the contributor restriction dialog
     */
    openContributorRestrictionDialog: () => void

    /**
     * Closes the contributor restriction dialog
     */
    closeContributorRestrictionDialog: () => void
}

/**
 * Zustand store for managing PT activation warning dialog state
 *
 * This store is the single source of truth for PT activation dialog state.
 * It manages dialog visibility, PT information, and user action callbacks.
 *
 * @example
 * // Using the store directly
 * const { isDialogOpen, ptNode, ptName, openDialog, closeDialog } = usePTActivationStore()
 *
 * // Open dialog for inactive PT
 * openDialog(ptElementNode, ['My Product Type'], 'content-123', async () => {
 *   await addPTToProject()
 * })
 *
 * // Close dialog
 * closeDialog()
 *
 * @example
 * // Using selector hooks (recommended for performance)
 * const isOpen = useIsPTActivationDialogOpen()
 * const { ptNode, ptName } = usePTActivationDialogData()
 * const { openDialog, closeDialog } = usePTActivationDialogActions()
 */
export const usePTActivationStore = create<PTActivationState>((set) => ({
    isDialogOpen: false,
    ptElementNode: null,
    ptName: [],
    contentItemId: null,
    onProceedCallback: null,
    isDeactivationDialogOpen: false,
    deactivationOnProceedCallback: null,
    isContributorRestrictionDialogOpen: false,
    updatedElementNodes: null,
    isProgressDialogOpen: false,
    activatingPTName: '',

    openDialog: (ptElementNode, ptName, contentItemId, onProceed) => {
        set({
            isDialogOpen: true,
            ptElementNode,
            ptName,
            contentItemId,
            onProceedCallback: onProceed,
        })
    },

    closeDialog: () => {
        set({
            isDialogOpen: false,
            ptElementNode: null,
            ptName: [],
            contentItemId: null,
            onProceedCallback: null,
        })
    },

    openDeactivationDialog: (onProceed) => {
        set({
            isDeactivationDialogOpen: true,
            deactivationOnProceedCallback: onProceed,
        })
    },

    closeDeactivationDialog: () => {
        set({
            isDeactivationDialogOpen: false,
            deactivationOnProceedCallback: null,
        })
    },

    setUpdatedElementNodes: (nodes) => {
        set({ updatedElementNodes: nodes })
    },

    openProgressDialog: (ptName) => {
        set({ isProgressDialogOpen: true, activatingPTName: ptName })
    },

    closeProgressDialog: () => {
        set({ isProgressDialogOpen: false, activatingPTName: '' })
    },

    openContributorRestrictionDialog: () => {
        set({ isContributorRestrictionDialogOpen: true })
    },

    closeContributorRestrictionDialog: () => {
        set({ isContributorRestrictionDialogOpen: false })
    },
}))

/**
 * A custom hook that retrieves a shallowly selected value from the PT activation store.
 *
 * Sample usage:
 * ```typescript
 * const { isDialogOpen, ptName } = usePTActivationShallowSelector((state) => ({
 *   isDialogOpen: state.isDialogOpen,
 *   ptName: state.ptName,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const usePTActivationShallowSelector = <T>(
    selector: (state: PTActivationState) => T
): T => usePTActivationStore(useShallow(selector))

/**
 * Selector hook: Get whether the PT activation dialog is open
 *
 * @returns true if the dialog is open, false otherwise
 */
export const useIsPTActivationDialogOpen = () =>
    usePTActivationStore((state) => state.isDialogOpen)

/**
 * Selector hook: Get the PT element node and name for display
 *
 * @returns Object containing ptElementNode and ptName
 */
export const usePTActivationDialogData = () =>
    usePTActivationShallowSelector((state) => ({
        ptElementNode: state.ptElementNode,
        ptName: state.ptName,
        onProceedCallback: state.onProceedCallback,
    }))

/**
 * Selector hook: Get dialog action functions
 *
 * @returns Object containing openDialog and closeDialog functions
 */
export const usePTActivationDialogActions = () =>
    usePTActivationShallowSelector((state) => ({
        openDialog: state.openDialog,
        closeDialog: state.closeDialog,
    }))

/**
 * Selector hook: Get whether the PT deactivation dialog is open
 *
 * @returns true if the deactivation dialog is open, false otherwise
 */
export const useIsPTDeactivationDialogOpen = () =>
    usePTActivationStore((state) => state.isDeactivationDialogOpen)

/**
 * Selector hook: Get the deactivation callback
 *
 * @returns Object containing deactivationOnProceedCallback
 */
export const usePTDeactivationDialogData = () =>
    usePTActivationShallowSelector((state) => ({
        deactivationOnProceedCallback: state.deactivationOnProceedCallback,
    }))

/**
 * Selector hook: Get deactivation dialog action functions
 *
 * @returns Object containing openDeactivationDialog and closeDeactivationDialog functions
 */
export const usePTDeactivationDialogActions = () =>
    usePTActivationShallowSelector((state) => ({
        openDeactivationDialog: state.openDeactivationDialog,
        closeDeactivationDialog: state.closeDeactivationDialog,
    }))

/**
 * Selector hook: Get whether the contributor restriction dialog is open
 *
 * @returns true if the contributor restriction dialog is open, false otherwise
 */
export const useIsContributorRestrictionDialogOpen = () =>
    usePTActivationStore((state) => state.isContributorRestrictionDialogOpen)

/**
 * Selector hook: Get contributor restriction dialog action functions
 *
 * @returns Object containing openContributorRestrictionDialog and closeContributorRestrictionDialog functions
 */
export const useContributorRestrictionDialogActions = () =>
    usePTActivationShallowSelector((state) => ({
        openContributorRestrictionDialog: state.openContributorRestrictionDialog,
        closeContributorRestrictionDialog: state.closeContributorRestrictionDialog,
    }))
